import { useState } from "react";
import api, { apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";

const Row = ({ inv, picked, targets, onPick, onToggle }) => (
  <div className="p-2 text-xs hover:bg-slate-50" data-testid={`portfolio-confirm-row-${inv.id}`}>
    <label className="flex items-center gap-3">
      <Checkbox checked={picked} onCheckedChange={(v) => onPick(!!v)}
                data-testid={`portfolio-confirm-inv-${inv.id}`} />
      <span className="font-medium w-28 truncate">{inv.number || "sans n°"}</span>
      <span className="text-slate-500 w-20">{fmtDate(inv.invoice_date)}</span>
      <span className="flex-1 truncate">{inv.party_name}</span>
      <span className="font-mono">{fmtEUR(inv.amount_ht)} HT</span>
    </label>
    <div className="mt-1 ml-7 flex flex-wrap gap-1">
      {inv.contracts.map((c) => (
        <button key={c.id} type="button" onClick={() => onToggle(c.id)}
                className={cn("rounded-full border px-2 py-0.5 text-[10px] transition-colors",
                  targets.includes(c.id) ? "bg-blue-600 border-blue-600 text-white"
                                         : "bg-white text-slate-600 hover:border-blue-400")}
                data-testid={`portfolio-confirm-target-${inv.id}-${c.id}`}>
          {c.title || "Contrat"} · {fmtEUR(c.amount_ht)}
        </button>
      ))}
    </div>
  </div>
);

// Une seule fenêtre pour toutes les factures du portefeuille au montant non concordant.
export default function PortfolioConfirmDialog({ data, onClose }) {
  const [picked, setPicked] = useState({});
  const [targets, setTargets] = useState(() => Object.fromEntries(
    data.to_confirm.map((i) => [i.id, [i.suggested_contract_id]])));
  const [busy, setBusy] = useState(false);
  const ids = Object.keys(picked).filter((k) => picked[k] && targets[k]?.length);

  const toggle = (invId, cid) => setTargets((t) => {
    const cur = t[invId] || [];
    return { ...t, [invId]: cur.includes(cid) ? cur.filter((x) => x !== cid) : [...cur, cid] };
  });

  const submit = async () => {
    setBusy(true);
    let ok = 0;
    for (const id of ids) {
      const [first, ...rest] = targets[id];
      try {
        await api.post(`/contracts/${first}/link-invoice`, { invoice_id: id, contract_ids: rest });
        ok += 1;
      } catch (e) { toast.error(apiError(e)); }
    }
    setBusy(false);
    toast.success(`${ok} facture(s) liée(s)`);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="portfolio-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Montants non concordants ({data.to_confirm_count})</DialogTitle>
          <DialogDescription>
            Ces factures ne correspondent au montant d'aucun contrat du client. Coche celles à
            rapprocher quand même et choisis le ou les contrats (en bleu).
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 text-xs">
          <button className="text-blue-600 hover:underline" data-testid="portfolio-confirm-all"
                  onClick={() => setPicked(Object.fromEntries(data.to_confirm.map((i) => [i.id, true])))}>
            Tout cocher
          </button>
          <button className="text-slate-500 hover:underline" onClick={() => setPicked({})}
                  data-testid="portfolio-confirm-none">Tout décocher</button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto divide-y border rounded">
          {data.to_confirm.map((inv) => (
            <Row key={inv.id} inv={inv} picked={!!picked[inv.id]} targets={targets[inv.id] || []}
                 onPick={(v) => setPicked((p) => ({ ...p, [inv.id]: v }))}
                 onToggle={(cid) => toggle(inv.id, cid)} />
          ))}
        </div>
        {data.to_confirm_count > data.to_confirm.length && (
          <p className="text-[11px] text-slate-500">
            {data.to_confirm.length} premières affichées sur {data.to_confirm_count}.
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="portfolio-confirm-cancel">Ignorer</Button>
          <Button onClick={submit} disabled={!ids.length || busy} data-testid="portfolio-confirm-submit">
            {busy && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
            Lier {ids.length || ""} facture(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
